import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { inject } from '@angular/core';
import { environment } from '@environments/environment';
import { checkIsDemoOrg } from 'src/app/core/models/organizations';

@Injectable()
export class VssAnalyticsService {
  private http = inject(HttpClient);

  private buildUrl(endpoint: string, organizationId: number, params: any = {}) {
    let demostr = checkIsDemoOrg() ? "/demo" : "";
    let url = `${environment.apiUrl}${demostr}/v1/vicarius/analytics/${endpoint}(${organizationId})/`;
    let httpParams = new HttpParams();
    for (const key in params) {
      if (params.hasOwnProperty(key) && params[key] !== null) {
        httpParams = httpParams.set(key, params[key]);
      }
    }
    return { url, params: httpParams };
  }

  // API Methods for VSS data
  public getVssOverviewData(organizationId: number | null, params?: any) {
    if (!organizationId) {
      throw new Error('Organization ID is required');
    }
    const { url, params: httpParams } = this.buildUrl('vulnerabilities', organizationId, params);
    return this.http.get<VssOverviewDataApiResponse>(url, { params: httpParams });
  }

  // Removed getProblemChildPatches - this endpoint doesn't exist and shouldn't be called

  public getCriticalVulnerabilityAging(organizationId: number | null, params?: any) {
    if (!organizationId) {
      throw new Error('Organization ID is required');
    }
    const { url, params: httpParams } = this.buildUrl('criticalvulnerabilityaging', organizationId, params);
    return this.http.get<CriticalVulnerabilityAgingApiResponse>(url, { params: httpParams });
  }

  public getAverageCoverage(organizationId: number | null, params?: any) {
    if (!organizationId) {
      throw new Error('Organization ID is required');
    }
    const { url, params: httpParams } = this.buildUrl('averagecoverage', organizationId, params);
    return this.http.get<AverageCoverageApiResponse>(url, { params: httpParams });
  }

  public getAverageCompliance(organizationId: number | null, params?: any) {
    if (!organizationId) {
      throw new Error('Organization ID is required');
    }
    const { url, params: httpParams } = this.buildUrl('compliancerate', organizationId, params);
    return this.http.get<AverageComplianceApiResponse>(url, { params: httpParams });
  }

  public getAverageMeanTimeToMitigate(organizationId: number | null, params?: any) {
    if (!organizationId) {
      throw new Error('Organization ID is required');
    }
    const { url, params: httpParams } = this.buildUrl('averagemeantimetomitigate', organizationId, params);
    return this.http.get<AverageMeanTimeToMitigateApiResponse>(url, { params: httpParams });
  }

  public getVulnerabilityEvents(organizationId: number | null, params?: any) {
    if (!organizationId) {
      throw new Error('Organization ID is required');
    }
    const { url, params: httpParams } = this.buildUrl('vulnerabilityevents', organizationId, params);
    return this.http.get<VulnerabilityEventsApiResponse>(url, { params: httpParams });
  }

  public getDeviceTypeChart(organizationId: number | null, params?: any) {
    if (!organizationId) {
      throw new Error('Organization ID is required');
    }
    const { url, params: httpParams } = this.buildUrl('vulnerabilitycountbydevicetype', organizationId, params);
    return this.http.get<DeviceTypeChartApiResponse>(url, { params: httpParams });
  }

  public getMttmAnalysis(organizationId: number | null, params?: any) {
    if (!organizationId) {
      throw new Error('Organization ID is required');
    }
    const { url, params: httpParams } = this.buildUrl('criticalandhighmttmanalysis', organizationId, params);
    return this.http.get<MttmAnalysisApiResponse>(url, { params: httpParams });
  }
}

export interface VssOverviewDataApiResponse {
  patchable: number;
  patched_percentage: number;
  unpatchable: number;
  unpatchable_percentage: number;
  total_instances: number;
  unique_cves: number;
  percent_consolidated: number;
  last_fetch_datetime: string; // Format: "2025-04-01 18:31:37"
}

// Removed ProblemChildPatchesApiResponse - not needed, using datatable endpoint instead

export interface CriticalVulnerabilityAgingApiResponse {
  totalCriticalVulnerabilities: number;
  ageRanges: Array<{
    range: string;
    count: number;
    percentage: number;
    patchable: number;
    color: string;
  }>;
  last_fetch_datetime: string;
}

export interface AverageCoverageApiResponse {
  current_average_coverage: number;
  previous_average_coverage: number;
  percentage_difference: number;
}

export interface AverageComplianceApiResponse {
  current_compliance_rate: number;
  previous_compliance_rate: number;
  percentage_difference: number;
}

export interface AverageMeanTimeToMitigateApiResponse {
  current_average_mean_time_to_mitigate: number;
  previous_average_mean_time_to_mitigate: number;
  mttm_days_difference: number;
}

export interface VulnerabilityEventsApiResponse {
  events: Array<{
    date: string; // Format: "2025-04-01 18:31:37"
    detected: number;
    mitigated: number;
  }>;
  patchingStats: {
    patchedPercentage: number;
  };
}

export interface DeviceTypeChartApiResponse {
  data: Array<{
    device_type: string;
    device_count: number;
    vulnerability_count: number;
  }>;
}

export interface MttmAnalysisApiResponse {
  mttm_trend: Array<{
    date: string; // Format: "2025-04-01 18:31:37"
    critical: number;
    high: number;
  }>;
  current_critical_average_mttm: number;
  previous_critical_average_mttm: number;
  critical_average_mttm_difference: number;
  current_high_average_mttm: number;
  previous_high_average_mttm: number;
  high_average_mttm_difference: number;
}
